import { REVEAL_TRIGGER_WAIT_MS, REVEAL_WAIT_MS } from "./constants";
import { resolveTargetElement } from "./target-resolver";
import { waitForElement } from "./wait-for-element";
import type { TourStepReveal } from "../types/tour.types";

const isExpanded = (element: HTMLElement): boolean =>
  element.getAttribute("aria-expanded") === "true";

const isActionable = (element: HTMLElement): boolean =>
  element.getAttribute("aria-disabled") !== "true" &&
  !(element as HTMLButtonElement).disabled;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const rightClick = (element: HTMLElement): void => {
  const rect = element.getBoundingClientRect();
  const clientX = Math.round(
    clamp(rect.left + rect.width / 2, 8, window.innerWidth - 8),
  );
  const clientY = Math.round(
    clamp(rect.top + Math.min(rect.height / 2, 140), 8, window.innerHeight - 8),
  );

  element.dispatchEvent(
    new MouseEvent("contextmenu", {
      bubbles: true,
      cancelable: true,
      view: window,
      button: 2,
      buttons: 2,
      clientX,
      clientY,
    }),
  );
};

const dismissMenu = (reveal: TourStepReveal): void => {
  const menu = reveal.awaits ? resolveTargetElement(reveal.awaits) : null;
  (menu ?? window.document.body).dispatchEvent(
    new KeyboardEvent("keydown", {
      key: "Escape",
      code: "Escape",
      bubbles: true,
      cancelable: true,
    }),
  );
};

export const openReveal = async (
  reveal: TourStepReveal,
): Promise<TourStepReveal | null> => {
  let trigger = resolveTargetElement(reveal.trigger);
  if (!trigger) return null;

  if (!isActionable(trigger)) {
    const ready = await waitForElement(() => {
      const element = resolveTargetElement(reveal.trigger);
      return element && isActionable(element) ? element : null;
    }, REVEAL_TRIGGER_WAIT_MS).promise;
    if (!ready) return null;
    trigger = ready;
  }

  if (reveal.via === "contextmenu") {
    rightClick(trigger);
  } else if (!isExpanded(trigger)) {
    trigger.click();
  }

  if (reveal.awaits) {
    const key = reveal.awaits;
    await waitForElement(() => resolveTargetElement(key), REVEAL_WAIT_MS)
      .promise;
  }

  return reveal;
};

export const closeReveal = (reveal: TourStepReveal): void => {
  window.requestAnimationFrame(() => {
    if (reveal.via === "contextmenu") {
      dismissMenu(reveal);
      return;
    }
    const trigger = resolveTargetElement(reveal.trigger);
    if (trigger && isExpanded(trigger)) trigger.click();
  });
};
